import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { SpareRequisition } from '../../modules/spares/requisitions/useData';
import ErrorDialog from '../../components/ui/ErrorDialog';
import { duplicateSpareRequisition } from '../../services/mutations';
import {
  getColumns,
  getToolbarIcon,
  isSpareRequisitionDeleteBlockedByCaseLink,
  isSpareRequisitionLockedAfterPipelineStep,
  moduleConfig as baseRequisitionsModuleConfig,
} from '../../modules/spares/requisitions/requisitionsModuleConfig';

export { getColumns, getToolbarIcon };

type VesselRequisitionRoutes = {
  list: string;
  new: string;
  detail: (id: number) => string;
  edit: (id: number) => string;
};

export function createVesselSpareRequisitionsModuleConfig(vesselId: number) {
  const base = `/spare-requisitions`;
  void vesselId;
  const routes: VesselRequisitionRoutes = {
    list: base,
    new: `${base}/new`,
    detail: (id: number) => `${base}/${id}`,
    edit: (id: number) => `${base}/${id}/edit`,
  };

  const filtersWithoutVessel = {
    ...baseRequisitionsModuleConfig.filters,
    fields: baseRequisitionsModuleConfig.filters.fields.filter((f) => f.key !== 'vessel'),
    filterGroups: baseRequisitionsModuleConfig.filterGroups?.map((g) => ({
      ...g,
      fields: g.fields.filter((k) => k !== 'vessel'),
    })),
  };

  const moduleConfig = {
    ...baseRequisitionsModuleConfig,
    module: {
      ...baseRequisitionsModuleConfig.module,
      permissionModuleKey: 'vessel_spare_requisitions',
      route: routes,
    },
    filters: filtersWithoutVessel,
    toolbar: {
      actions: baseRequisitionsModuleConfig.toolbar.actions.map((a) =>
        a.key === 'add' ? { ...a, href: routes.new } : a,
      ),
    },
    table: {
      ...baseRequisitionsModuleConfig.table,
      onRowClick: (item: SpareRequisition) => {
        window.location.href = routes.detail(item.id);
      },
    },
  };

  function formatVesselReqListActionError(err: unknown, fallback: string): string {
    const data =
      err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { data?: { message?: string | string[] } } }).response?.data
        : undefined;
    const m = data?.message;
    return Array.isArray(m) ? m.join('; ') : typeof m === 'string' ? m : fallback;
  }

  const VESSEL_REQ_LIST_ERR = {
    editLocked:
      'This requisition cannot be edited while its spare case already has a supplier enquiry, quotation, or purchase order.',
    deleteLocked:
      'This requisition cannot be permanently deleted while a supplier enquiry, quotation, or purchase order is linked to it.',
    deleteRequiresUnlinkFromCase:
      'This requisition is on a spare case. Remove it from the case first, then you can delete it.',
    editNoPermission: 'You do not have permission to edit spare requisitions.',
    deleteNoPermission: 'You do not have permission to delete spare requisitions.',
    copyFailed: 'Could not copy requisition.',
  } as const;

  const VESSEL_REQ_ACTION_SLOT = 'flex h-9 w-9 shrink-0 items-center justify-center';

  const TableActionsCell: React.FC<{
    item: SpareRequisition;
    onDelete: (id: number) => void;
    pipelineLocked: boolean;
    canEditPermission: boolean;
    canDeletePermission: boolean;
  }> = ({ item, onDelete, pipelineLocked, canEditPermission, canDeletePermission }) => {
    const navigate = useNavigate();
    const [copyBusy, setCopyBusy] = React.useState(false);
    const [errorDialog, setErrorDialog] = React.useState<{ title: string; message: string } | null>(null);

    const caseBlocksDelete = isSpareRequisitionDeleteBlockedByCaseLink(item);
    const canEdit = !pipelineLocked && canEditPermission;
    const canDelete = !pipelineLocked && canDeletePermission && !caseBlocksDelete;

    const showError = (title: string, message: string) => setErrorDialog({ title, message });

    const onEditBlocked = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!canEditPermission) {
        showError('Permission denied', VESSEL_REQ_LIST_ERR.editNoPermission);
        return;
      }
      showError('Cannot edit', VESSEL_REQ_LIST_ERR.editLocked);
    };

    const onDeleteBlocked = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!canDeletePermission) {
        showError('Permission denied', VESSEL_REQ_LIST_ERR.deleteNoPermission);
        return;
      }
      if (pipelineLocked) {
        showError('Cannot delete', VESSEL_REQ_LIST_ERR.deleteLocked);
        return;
      }
      showError('Cannot delete', VESSEL_REQ_LIST_ERR.deleteRequiresUnlinkFromCase);
    };

    const onCopy = async (e: React.MouseEvent) => {
      e.stopPropagation();
      setCopyBusy(true);
      try {
        const created = (await duplicateSpareRequisition(item.id)) as SpareRequisition;
        if (created?.id) navigate(routes.edit(created.id));
      } catch (err: unknown) {
        showError('Could not copy', formatVesselReqListActionError(err, VESSEL_REQ_LIST_ERR.copyFailed));
      } finally {
        setCopyBusy(false);
      }
    };

    const editClass =
      'p-1 text-blue-600 hover:text-blue-900 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50';

    return (
      <>
        <ErrorDialog
          isOpen={errorDialog != null}
          onClose={() => setErrorDialog(null)}
          title={errorDialog?.title ?? 'Error'}
          message={errorDialog?.message ?? ''}
          zIndex={120}
        />
        <div className="flex items-center flex-nowrap gap-0">
          <div className={VESSEL_REQ_ACTION_SLOT}>
            <Link
              to={routes.detail(item.id)}
              className="p-1 text-indigo-600 hover:text-indigo-900 hover:bg-indigo-50 rounded-lg transition-colors"
              onClick={(e) => e.stopPropagation()}
              title="View"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                />
              </svg>
            </Link>
          </div>
          <div className={VESSEL_REQ_ACTION_SLOT}>
            <button
              type="button"
              disabled={copyBusy}
              className="p-1 text-teal-600 hover:text-teal-900 hover:bg-teal-50 rounded-lg transition-colors disabled:opacity-50"
              onClick={(e) => void onCopy(e)}
              title="Copy as new draft"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                />
              </svg>
            </button>
          </div>
          <div className={VESSEL_REQ_ACTION_SLOT}>
            {canEdit ? (
              <Link
                to={routes.edit(item.id)}
                className={editClass}
                onClick={(e) => e.stopPropagation()}
                title="Edit"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                  />
                </svg>
              </Link>
            ) : (
              <button type="button" className={editClass} onClick={onEditBlocked} title="Edit">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                  />
                </svg>
              </button>
            )}
          </div>
          <div className={VESSEL_REQ_ACTION_SLOT}>
            {canDelete ? (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(item.id);
                }}
                className="p-1 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-lg transition-colors"
                title="Delete requisition"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
              </button>
            ) : (
              <button
                type="button"
                className="p-1 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-lg transition-colors"
                onClick={onDeleteBlocked}
                title="Delete requisition"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
              </button>
            )}
          </div>
        </div>
      </>
    );
  };

  const getTableActions = (
    item: SpareRequisition,
    onDelete: (id: number) => void,
    _c?: unknown,
    _ctx?: unknown,
    options?: { canDelete?: boolean; canEdit?: boolean },
  ): React.ReactNode => {
    const locked = isSpareRequisitionLockedAfterPipelineStep(item);
    return (
      <TableActionsCell
        item={item}
        onDelete={onDelete}
        pipelineLocked={locked}
        canEditPermission={options?.canEdit !== false}
        canDeletePermission={options?.canDelete !== false}
      />
    );
  };

  return { moduleConfig, getTableActions, routes };
}
